import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import {
  Shield, Users, Calendar, Building, Heart, BarChart,
  ArrowRight, Globe, Award, TrendingUp, Radio,
  Plane, UserCheck, Target
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }
  }, [isInView, end]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// Services Data
const SERVICES = [
  {
    icon: Users,
    title: 'Sports Agency',
    description: 'Talent management, player representation and career development across Africa.',
    href: '/services#agency',
    color: 'from-green-600 to-green-700'
  },
  {
    icon: BarChart,
    title: 'Sports Technology',
    description: 'Digital platforms and solutions for sports organizations and federations.',
    href: '/services#technology',
    color: 'from-blue-600 to-blue-700'
  },
  {
    icon: Shield,
    title: 'Governance & Advisory',
    description: 'Strategic consulting for sports bodies, clubs and government agencies.',
    href: '/services#governance',
    color: 'from-purple-600 to-purple-700'
  },
  {
    icon: TrendingUp,
    title: 'Commercial & Sponsorship',
    description: 'Revenue generation, sponsorship acquisition and brand partnerships.',
    href: '/services#commercial',
    color: 'from-red-600 to-red-700'
  },
  {
    icon: Calendar,
    title: 'Events & Operations',
    description: 'End-to-end event management for tournaments, leagues and championships.',
    href: '/services#events',
    color: 'from-orange-600 to-orange-700'
  },
  {
    icon: Radio,
    title: 'Media & Communications',
    description: 'Sports media, PR, broadcasting rights and content production.',
    href: '/services#media',
    color: 'from-pink-600 to-pink-700'
  },
  {
    icon: Plane,
    title: 'Sports Tourism',
    description: 'Golf tourism, sports travel packages and destination experiences.',
    href: '/services#tourism',
    color: 'from-cyan-600 to-cyan-700'
  },
  {
    icon: UserCheck,
    title: 'Workforce Solutions',
    description: 'Sports industry recruitment, training and capacity building.',
    href: '/services#workforce',
    color: 'from-amber-600 to-amber-700'
  }
];

// Industries Data
const INDUSTRIES = [
  { name: 'Government', icon: Building },
  { name: 'Federations', icon: Shield },
  { name: 'Clubs', icon: Users },
  { name: 'Schools', icon: Award },
  { name: 'Universities', icon: Globe },
  { name: 'Sponsors', icon: TrendingUp },
  { name: 'NGOs', icon: Heart },
  { name: 'Investors', icon: BarChart }
];

// Why Michezonet Data
const WHY_MICHEZONET = [
  {
    icon: Target,
    title: 'Sports Expertise',
    description: 'Deep understanding of African sports landscape and stakeholders'
  },
  {
    icon: BarChart,
    title: 'Technology Innovation',
    description: 'Cutting-edge platforms designed for African sports organizations'
  },
  {
    icon: Shield,
    title: 'Governance Excellence',
    description: 'Best practices in sports administration and compliance'
  },
  {
    icon: TrendingUp,
    title: 'Commercial Success',
    description: 'Proven track record in sponsorship and revenue generation'
  }
];

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/cms/home`);
        setContent(res.data);
      } catch (err) {
        // Silent fail - use default content
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const { settings } = content || {};

  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section with Split Layout */}
      <section className="relative min-h-screen bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-200px)]">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              {/* Badge */}
              <span className="inline-block px-4 py-2 bg-[#008000]/20 border border-[#008000]/30 rounded-full text-[#00CC00] text-sm font-medium mb-6">
                The Future of Sports in Africa
              </span>
              
              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-white leading-tight mb-6">
                Creating Value Across{' '}
                <span className="bg-gradient-to-r from-[#008000] to-[#00CC00] bg-clip-text text-transparent">
                  Africa's Sports Ecosystem
                </span>
              </h1>
              
              {/* Tagline */}
              <p className="text-white/60 text-sm md:text-base font-medium tracking-wider mb-4">
                Sports Agency • Technology • Governance • Commercial Solutions
              </p>
              
              {/* Description */}
              <p className="text-white/70 text-lg md:text-xl mb-8 leading-relaxed max-w-xl">
                Michezonet is Africa's integrated sports agency delivering governance, technology, 
                commercial advisory, event management, media, tourism and digital transformation 
                solutions for governments, federations, clubs, brands, investors and event owners.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/contact"
                  data-testid="hero-request-proposal-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#008000]/25"
                >
                  Request Proposal
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/contact#consultation"
                  data-testid="hero-book-consultation-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all border border-white/20"
                >
                  Book Consultation
                </Link>
              </div>
              
              {/* Impact Stats - Inline */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { value: 50, suffix: '+', label: 'Organizations' },
                  { value: 200, suffix: '+', label: 'Events' },
                  { value: 10000, suffix: '+', label: 'Athletes' },
                  { value: 15, suffix: '+', label: 'Countries' }
                ].map((stat) => (
                  <div key={stat.label} className="text-left">
                    <p className="text-2xl md:text-3xl font-heading font-bold text-[#008000]">
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </p>
                    <p className="text-white/50 text-xs md:text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Image Container with Frame */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#008000]/20 border border-white/10">
                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-[#008000] rounded-tl-2xl z-10" />
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#008000] rounded-br-2xl z-10" />
                
                <img
                  src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&h=500&fit=crop"
                  alt="African Sports Excellence"
                  className="w-full aspect-video object-cover"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              </div>
              
              {/* Floating Stats Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 hidden lg:block"
              >
                <p className="text-[#00CC00] font-heading font-bold text-2xl">Africa&apos;s #1</p>
                <p className="text-white/70 text-sm">Sports Solutions Partner</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">About Michezonet</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mt-4 mb-6">
                Africa's Integrated Sports Agency & Business Solutions Company
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Founded with a vision to transform Africa's sports industry, Michezonet delivers 
                comprehensive solutions that bridge the gap between sports organizations and 
                commercial success. We combine deep sports expertise with technology innovation 
                to create lasting value for all stakeholders in the ecosystem.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                From government sports departments and national federations to clubs, schools, 
                sponsors and investors, we provide the strategic guidance, technology platforms 
                and operational excellence needed to achieve sustainable growth and impact.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#008000] hover:text-[#00CC00] font-semibold transition-colors"
              >
                Learn More About Us
                <ArrowRight size={18} />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {WHY_MICHEZONET.map((item, idx) => (
                <div 
                  key={item.title}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#008000]/50 transition-colors"
                >
                  <item.icon className="w-10 h-10 text-[#008000] mb-4" />
                  <h4 className="text-white font-heading font-bold mb-2">{item.title}</h4>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mt-4 mb-6">
              Integrated Sports Solutions
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Comprehensive services designed to transform sports organizations and create lasting value across the ecosystem.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, idx) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={service.href}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block h-full bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#008000]/50 hover:bg-white/10 transition-all group"
                >
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center mb-4`}>
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-white font-heading font-bold text-lg mb-2 group-hover:text-[#008000] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-[#008000] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn More <ArrowRight size={14} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
          >
            <div>
              <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Featured Projects</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4">
                Flagship Initiatives
              </h2>
            </div>
            <Link
              to="/projects"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-white/60 hover:text-[#008000] font-medium transition-colors"
            >
              View All Projects <ArrowRight size={18} />
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Road to SomaBay */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/somabay" data-testid="project-road-to-somabay" className="block group">
                <div className="relative h-64 rounded-xl overflow-hidden mb-4">
                  <img 
                    src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=600" 
                    alt="Road to SomaBay"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-[#FF0000] text-white text-xs font-bold px-2 py-1 rounded">GOLF</span>
                    <h3 className="text-white font-heading font-bold text-xl mt-2">Road to SomaBay</h3>
                    <p className="text-white/70 text-sm">Kenya's premier amateur golf circuit</p>
                  </div>
                </div>
              </Link>
            </motion.div>
            
            {/* MyGolfHub */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Link to="/platform#mygolfhub" data-testid="project-mygolfhub" className="block group">
                <div className="relative h-64 rounded-xl overflow-hidden mb-4">
                  <img 
                    src="https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=600" 
                    alt="MyGolfHub Africa"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-[#008000] text-white text-xs font-bold px-2 py-1 rounded">TECHNOLOGY</span>
                    <h3 className="text-white font-heading font-bold text-xl mt-2">MyGolfHub Africa</h3>
                    <p className="text-white/70 text-sm">Digital platform for African golf</p>
                  </div>
                </div>
              </Link>
            </motion.div>
            
            {/* League Management */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/platform" data-testid="project-league-management" className="block group">
                <div className="relative h-64 rounded-xl overflow-hidden mb-4">
                  <img 
                    src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600" 
                    alt="League Management"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">GOVERNANCE</span>
                    <h3 className="text-white font-heading font-bold text-xl mt-2">League Management</h3>
                    <p className="text-white/70 text-sm">Comprehensive league administration</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Event Video Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Featured Event</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-4">
              Road to SomaBay 2026
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Kenya&apos;s premier amateur golf circuit - watch our journey to the SomaBay World Cup grand finale in Egypt.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            {/* Video Container */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#008000]/20 border border-white/10 bg-gray-900">
              {/* Decorative corner accents */}
              <div className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2 border-[#008000] rounded-tl-2xl z-10" />
              <div className="absolute bottom-0 right-0 w-24 h-24 border-b-2 border-r-2 border-[#008000] rounded-br-2xl z-10" />
              
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full aspect-video object-cover"
              >
                <source src="/hero-video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Video overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
              
              {/* Live indicator */}
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full z-20">
                <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" />
                <span className="text-white text-xs font-medium">SomaBay 2026</span>
              </div>
            </div>
            
            {/* Video Info */}
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <p className="text-[#00CC00] font-heading font-bold text-3xl">6</p>
                <p className="text-white/60 text-sm">Qualifying Events</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <p className="text-[#00CC00] font-heading font-bold text-3xl">500+</p>
                <p className="text-white/60 text-sm">Golfers Competing</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <p className="text-[#00CC00] font-heading font-bold text-3xl">Egypt</p>
                <p className="text-white/60 text-sm">Grand Finale Destination</p>
              </div>
            </div>
            
            {/* CTA */}
            <div className="mt-8 text-center">
              <Link
                to="/somabay"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#008000]/25"
              >
                Learn More About Road to SomaBay
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Who We Serve</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6">
              Industries We Transform
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {INDUSTRIES.map((industry, idx) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 text-center hover:border-[#008000]/50 hover:bg-white/10 transition-all cursor-pointer"
              >
                <industry.icon className="w-10 h-10 text-[#008000] mx-auto mb-3" />
                <p className="text-white font-medium">{industry.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#008000] to-[#006600] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Ready to Transform Sport?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Partner with Africa's leading sports agency to unlock the full potential of your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-request-proposal-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#008000] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Request Proposal
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact#consultation"
                data-testid="cta-book-consultation-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Book Consultation
              </Link>
              <Link
                to="/about#partners"
                data-testid="cta-partner-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white/50 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Partner With Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default HomePage;
